import { CAAS_INDEX_SUFFIX, DISABLE_ADS, IS_DEV_SITE } from "@app/constants";
import {
  GA4_REF_CODE_ANCHORBLOCK_B,
  GA4_REF_CODE_CONTENTBLOCK_C,
  GA4_REF_CODE_CONTENTBLOCK_J,
  GA5_REF_CODE_CONTENTHUB_BLOCK,
} from "@app/utils/ga/ref-code";
import {
  BRIGHTCOVE_CONFIG,
  DEFAULT_CONFIG_GAM,
  LOTAME_CONFIG,
  VWO_CONFIG,
} from "@src/pubs/common/config";

import {
  ConfigAboutUs,
  ConfigAffiliateLibrary,
  ConfigAffiliatesDeclaration,
  ConfigAffiliateShopping,
  ConfigAffiliatesProduct,
  ConfigAmazonAffiliates,
  ConfigAuthorProfile,
  ConfigBrightcove,
  ConfigCaas,
  ConfigCategory,
  ConfigContentHub,
  ConfigError,
  ConfigExploreMore,
  ConfigGAM,
  ConfigGTM,
  ConfigHome,
  ConfigLatestStory,
  ConfigLotame,
  ConfigNewsletter,
  ConfigRss,
  ConfigSdkAds,
  ConfigSearch,
  ConfigSingleStory,
  ConfigSite,
  ConfigSkimlinks,
  ConfigTaboola,
  ConfigTag,
  ConfigTeaser,
  ConfigTrackingCode,
  ConfigVideoLanding,
  ConfigVideoOverlayPortrait,
  ConfigVWO,
} from "../types/config";
import ContentHubShadow from "./components/ContentHubShadow";
import ContentHubShadowTeasers from "./components/ContentHubShadowTeasers";
import ContentListCarousel from "./components/ContentListCarousel";
import ContentListSecTag from "./components/ContentListSecTag";
import FadingCarousel from "./components/FadingCarousel";
import HomeCarousel from "./components/HomeCarousel";
import MagNewsletter from "./components/MagNewsletter";
import MastheadNUYOU from "./components/MastheadNUYOU";
import TrendingCarousel from "./components/TrendingCarousel";
import callOnQuoteSvg from "./icons/callOnQuote.svg?url";
import callOnQuoteSvgBtm from "./icons/callOnQuoteBtm.svg?url";
import {
  carouselLeftIcon,
  carouselRightIcon,
  moreIconPinkBrand,
} from "./icons/commonIcons";
import logoPath from "./icons/logo.svg?url";
import {
  copyIconBrown,
  facebookIcon,
  mailIcon,
  whatsappIcon,
} from "./icons/socialIcons";

export const configSite: ConfigSite = {
  // Google-Extended (Google News AI) pilot - DMG-6622.
  aiCrawlerAllowed: true,
  name: "NUYOU Singapore",
  address: import.meta.env.VITE_PUBLIC_BASE_URL || "https://www.nuyou.com.sg", // without trailing slash
  tagline: "最懂你的贴心好友",
  description:
    "新加坡女性生活杂志指标《女友》，从情感故事、职场洞察、时尚美容到吃喝玩乐—以贴心真诚的态度，持续为你提供资讯与陪伴。",
  titleSeparator: "|",
  logoPath,
  sectionLevel3: true,
  twitterSite: "@nuyousingapore",
  pageShare: {
    size: 35,
    media: [
      {
        name: "Facebook",
        icon: facebookIcon,
      },
      {
        name: "Whatsapp",
        icon: whatsappIcon,
      },
      {
        name: "Email",
        icon: mailIcon,
      },
      {
        name: "Copy",
        icon: copyIconBrown,
      },
    ],
  },
  splitZHSectionName: true,
  newsticker: {
    label: "联合早报精选:",
    rssUrl: "https://www.zaobao.com.sg/rss/realtime.xml",
    duration: 300,
  },
  aboutUs: {
    headlineBelow: true,
  },
  socialMedia: {
    title: "关注我们",
    pageWithAlternateIconColor: ["about-us", "404"],
  },
  stickyElement: "main",
  loadMore: {
    text: "加载故事",
    variant: "bouncingBall",
  },
  lang: "zh-SG",
  meta: [
    { name: "msapplication-TileColor", content: "#000000" },
    { name: "theme-color", content: "#ffffff" },
  ],
  links: [
    {
      rel: "icon",
      type: "image/png",
      sizes: "96x96",
      href: "/favicon-96x96.png",
    },
    { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
    { rel: "shortcut icon", href: "/favicon.ico" },
  ],
};

export { configHeader } from "./config/configHeader";

export const configCaas: ConfigCaas = {
  index: `nuyou${CAAS_INDEX_SUFFIX}`,
  tagIdPrefix: "tag:nuyou-keywords@sph.com.sg,2023:",
  cuePublicationName: "nuyou",
};

export const configGTM: ConfigGTM = {
  container: "GTM-K3KPT5F",
  siteVerification: "_cGEdj2ZMeQhb_OTIAKvlCGbSxaBwct6M6XrDxywcXM",
};

export const configGAM: ConfigGAM = {
  ...DEFAULT_CONFIG_GAM,
  account: "/5908/nuyou",
  useTopLeaderboard: true,
};

export const configSdkAds: ConfigSdkAds = { enabled: true };

export const configLotame: ConfigLotame = {
  ...LOTAME_CONFIG,
  active: !DISABLE_ADS,
};

export const configSkimlinks: ConfigSkimlinks = {
  active: !DISABLE_ADS,
  skimlinkId: "157725X1768903",
  geniuslinkId: "394419",
};

export const configVWO: ConfigVWO = {
  ...VWO_CONFIG,
  active: false,
};

export const configBrightcove: ConfigBrightcove = {
  ...BRIGHTCOVE_CONFIG,
};

export const configSingleStory: ConfigSingleStory = {
  CustomMasthead: MastheadNUYOU,
  authorProfile: true,
  byline: {
    defaultCredit: "NUYOU Team",
    moveShareAndBylineToDescription: true,
  },
  configShareArticle: {
    label: "分享文章:",
    divider: {
      top: false,
    },
  },
  displayTags: {
    displayTagHeading: true,
  },
  featureStory: {
    bodyMaxWidth: 830,
    imageInsideMasthead: true,
    skipCoverCaption: true,
  },
  gallery: {
    paginationSeparator: "/",
    galleryItemShareMedia: ["Facebook", "X", "Whatsapp", "Pinterest"],
  },
  quote: {
    svg: callOnQuoteSvg,
    svgBtm: callOnQuoteSvgBtm,
  },
  relatedArticle: {
    headerText: "Related Stories",
    headerTextZH: "更多内容",
    hasRelatedPrefix: false,
    cardLayout: {
      defaultVariant: "card-list-inverted-square",
      mobileUp: {
        defaultVariant: "card-list-inverted-square-mobile-up",
      },
    },
  },
  readMore: "阅览文章",
  readMoreButtonVariant: "primary",
  leaderboardAfterReadMore: true,
  reactionWidget: true,
};

export const configContentHub: ConfigContentHub = {
  configAds: {
    showInlineAds: true,
  },
  cardLayout: {
    listing: {
      defaultVariant: "card-stacked-original",
      mobileUp: {
        defaultVariant: "card-stacked-original-mobile-up",
      },
    },
  },
  configMasthead: {
    heading: {
      display: false,
    },
  },
  configBodyContent: {
    withHeading: true,
    display: true,
  },
  configShareButton: {
    display: true,
    label: "分享文章",
  },
  configEditorsNote: {
    display: false,
  },
  configCallQoute: {
    display: true,
  },
  configFeaturedSection: {
    display: true,
  },
  configDeals: {
    display: false,
    props: {
      heading: "editor's picks",
      noOfItemsInFrame: 4,
    },
  },
  storiesPerPage: {
    topStories: 0,
    totalStories: 6,
  },
  configTopStories: {
    display: true,
    firstItemAnchorBlock: false,
    fallback: false,
  },
  configListingStories: {
    isMasonry: true,
  },
  shouldExtracts: {
    anchorsWithPullQuote: ["topicWithTag-"],
    anchorsWithToc: ["topicWithTag-"],
  },
  layout: {
    stories: {
      elements: [
        {
          Component: ContentListCarousel,
          name: "contentListBlock",
          key: "topic-0",
          fallback: false,
        },
        {
          Component: ContentListSecTag,
          name: "contentListBlock",
          key: "sectag-0",
          limit: 6,
        },
        { name: "adsMidContent" },
        {
          Component: FadingCarousel,
          name: "contentListBlock",
          key: "topicWithTag",
          limit: 3,
        },
        { name: "videooverlay", limit: 8 },
        {
          Component: ContentListSecTag,
          name: "contentListBlock",
          key: "sectag-1",
          limit: 6,
        },
        { name: "adsMidContent" },
      ],
    },
    variant: [
      {
        name: "variant-shadow",
        CustomPrimaryBody: ContentHubShadow,
        elements: [
          {
            Component: ContentHubShadowTeasers,
            name: "contentListBlock",
            key: "sectag-0",
            limit: 6,
          },
          { name: "adsMidContent" },
          {
            Component: ContentListCarousel,
            name: "contentListBlock",
            key: "topic-0",
            fallback: false,
          },
          { name: "adsMidContent" },
          { name: "videooverlay", limit: 8 },
          { name: "adsMidContent" },
          {
            Component: FadingCarousel,
            name: "contentListBlock",
            key: "topicWithTag",
            limit: 3,
          },
        ],
      },
    ],
  },
  hasContentList: true,
  videoGalleryLandscape: {
    display: true,
  },
};

export const configLatestStory: ConfigLatestStory = {
  latestStoryLimit: 13,
  subAnchorLimit: 5,
  landscapeIndexes: [0, 1, 2, 3],
  mainContentBlockDisplay: "columns",
  mainContentBlockMode: "original",
  errorLayoutMode: "landscape",
  portraitImageWidth: 285,
  landscapeImageWidth: 345,
  hasAnchorBlock: true,
  customTitle: "Latest",
  redirectTarget: "/",
  redirectStatusCode: 302,
};

export const configCategory: ConfigCategory = {
  pinnedTeasersLimit: 4,
  latestStoriesLimit: 8,
  adPositioning: 3,
  hasDoubleSideBarAds: true,
  hideChildSection: true,
  hideSubmenuDivider: true,
  listGroupVariant: "wrap",
  layoutGrid: [
    { name: "introBlock" },
    { name: "anchorCarousel" },
    { name: "storyListingBlock" },
  ],
  card: {
    anchorblock: {
      defaultVariant: "anchorblock-stacked",
      mobileUp: {
        defaultVariant: "anchorblock-stacked-mobile-up",
      },
    },
    subAnchor: {
      defaultVariant: "card-stacked-portrait",
      mobileUp: {
        defaultVariant: "card-stacked-landscape-mobile-up",
      },
    },
    latestArticle: {
      defaultVariant: "card-stacked-original",
      mobileUp: {
        defaultVariant: "card-stacked-original-mobile-up",
      },
    },
  },
};

export const configTag: ConfigTag = {
  topStoriesLimit: 0,
  listedStoriesLimit: 8,
};

export const configAmazonAffiliates: ConfigAmazonAffiliates = {
  partnerTag: IS_DEV_SITE ? "sphmedia-dev-22" : "sphmedia-nyu25-22",
};

export const configTaboola: ConfigTaboola = {
  enabled: true,
  publisherId: "sphmedia-nuyou",
};

export const configSearch: ConfigSearch = {
  placeholder: "搜索",
  sortByText: "筛选:",
  sortByOptions: [
    { label: "相关性", value: "score" },
    { label: "最新", value: "latest" },
  ],
  noResultsText: {
    prefix: "抱歉，无法搜索到与",
    suffix: "的相关内容。",
  },
  withResultsText: "篇文章",
};

export const configExploreMore: ConfigExploreMore = {
  heading: "More Topics",
  headingZH: "更多主题",
  skipImage: true,
  hideDivider: true,
};

export const configVideoLanding: ConfigVideoLanding = {
  videoPlaylistFillUpSize: 9,
  videoPath: "/video",
};

export const configVideoOverlayPortrait: ConfigVideoOverlayPortrait = {
  stageButtonText: "查看更多",
};

export const configHome: ConfigHome = {
  teaser: {
    displayPublishDate: true,
    withTags: true,
    withDescription: true,
  },
  headingDivider: true,
  isHeadingClickable: true,
  anchorsWithDescription: [
    "pinned",
    "section-people",
    "section-lifestyle",
    "section-celebs",
    "section-style",
    "section-beauty",
    "topic",
    "trending",
  ],
  viewMore: { icon: moreIconPinkBrand },
  latestArticle: {
    mobileMode: "landscape",
    landscapeIndexes: [0, 1, 2, 3],
    viewMore: {
      text: "Read More",
    },
  },
  contentBlockD: {
    descriptionInTitle: false,
    crop: "landscape",
  },
  contentBlockCarousel: {
    leftIcon: carouselLeftIcon,
    rightIcon: carouselRightIcon,
  },
  videoGalleryLandscape: {
    hasPagination: true,
    viewMore: {
      href: "/video",
    },
    spotLight: {
      mobileLoopingArrowsBelow: true,
    },
  },
  contentColumnSideScroll: {
    section: "shopping",
  },
  affiliateProduct: {
    description: "",
    carouselWidthLeeWay: 20,
    itemWidth: 223,
  },
  magazineListing: {
    midDivider: false,
  },
  newsletter: {
    newsletterTitle: "免费订阅《女友》电子周报，即时获得网站最新咨询。",
    newsletterCtaText: "即刻订阅",
    newsletterLang: "zh",
  },
  trending: {
    limit: 4,
  },
  layoutGrid: {
    elements: [
      {
        name: "anchorCarousel",
        dataSource: {
          type: "list",
          key: "pinned",
          fallback: "latest",
          limit: 5,
        },
      },
      {
        name: "contentBlockN",
        dataSource: {
          type: "section",
          key: "people",
          refCode: "block-a",
          limit: 5,
        },
      },
      {
        name: "videoOverlay",
        dataSource: {
          type: "list",
          key: "videoplaylist",
          refCode: "videoplaylist",
          fallback: "videoplaylist",
          limit: 8,
        },
      },
      {
        Component: HomeCarousel,
        dataSource: {
          type: "tag",
          key: "topic-0",
          refCode: "contenthub",
          limit: 10,
          min: 1,
        },
      },
      {
        name: "adUnit",
      },
      {
        name: "contentBlockN",
        dataSource: {
          type: "section",
          key: "lifestyle",
          refCode: "block-b",
          limit: 5,
        },
      },
      {
        Component: TrendingCarousel,
        dataSource: {
          type: "list",
          key: "trending",
          fallback: "trending",
          refCode: "trending",
          limit: 9,
        },
      },
      {
        Component: HomeCarousel,
        dataSource: {
          type: "tag",
          key: "topic-1",
          refCode: "contenthub",
          limit: 10,
          min: 1,
        },
      },
      {
        name: "socialVideoPortrait",
        dataSource: {
          type: "list",
          key: "socialvideos",
        },
      },
      {
        name: "contentBlockN",
        dataSource: {
          type: "section",
          key: "celebs",
          refCode: "block-c",
          limit: 5,
        },
      },
      {
        name: "contentBlockN",
        dataSource: {
          type: "section",
          key: "style",
          refCode: "block-d",
          limit: 5,
        },
      },
      {
        name: "contentBlockN",
        dataSource: {
          type: "section",
          key: "beauty",
          refCode: "block-e",
          limit: 5,
        },
      },
      {
        name: "affiliateProduct",
        dataSource: {
          type: "list",
          key: "deals",
        },
        alternateBg: true,
      },
      {
        Component: MagNewsletter,
      },
    ],
  },
  trendingStories: { publisher: "nuyou" },
  socialVideoPortrait: {
    variant: "slider",
    target: "_blank",
  },
};

export const configNewsletter: ConfigNewsletter = {
  midasConsents: {
    newsletter: "nuyou_newsletter",
    marketing: ["nuyou_marketing_email", "sph_marketing_email"],
  },
  title: `免费订阅《女友》电子周报，即时获得网站最新咨询。`,
  body: ``,
  buttonVariant: "primary",
  signUpText: "即刻订阅",
  emailPlaceHolderText: "输入电邮",
  lang: "zh",
  subscriptionMessage: {
    title: "订阅成功！",
    body: "欢迎加入我们的社区！您将第一时间收到最新资讯、独家内容和独到见解，直接发送到您的邮箱。\n\n请留意查收，您的第一封电子报即将发送到您的邮箱！",
  },
};

export const configTeaser: ConfigTeaser = {
  publishDate: false,
  withDescription: true,
  withTags: true,
  anchorBlock: {
    publishDate: false,
  },
};

export const configTrackingCode: ConfigTrackingCode = {
  category: {
    listGroup: GA4_REF_CODE_ANCHORBLOCK_B,
  },
  contenthub: {
    block: {
      contentListBlock: GA5_REF_CODE_CONTENTHUB_BLOCK,
      contentBlockF: GA4_REF_CODE_CONTENTBLOCK_J,
    },
    featured: GA4_REF_CODE_CONTENTBLOCK_C,
    listing: GA5_REF_CODE_CONTENTHUB_BLOCK,
  },
};

export const configAffiliatesDeclaration: ConfigAffiliatesDeclaration = {};

export const configError: ConfigError = {
  relatedTitle: "Perhaps what you are looking for is here.",
  messages: {
    status404: {
      title: "找不到您要查找的页面。",
      lead: "该页面不存在。请重试或返回主页。",
    },
  },
};

export const configAuthorProfile: ConfigAuthorProfile = {
  showReadMore: true,
  hideDivider: true,
  listingLayout: {
    mainContentBlockDisplay: "columns",
    mainContentBlockMode: "original",
    imageSize: "big",
  },
  hideAds: false,
  hideSidebar: false,
  teaserLimit: 8,
};

export const configAboutUs: ConfigAboutUs = {
  profileBlock: {
    imageFill: "cover",
  },
  mobileImage: "landscape",
  isOverlay: true,
};

export const configAffiliatesProduct: ConfigAffiliatesProduct = {
  cta: {
    label: "点击购买",
  },
};
export const configAffiliateShopping: ConfigAffiliateShopping = {};

export const configRss: ConfigRss = [];

export const configAffiliateLibrary: ConfigAffiliateLibrary = {
  enable: true,
  lang: "zh_hans",
};
